import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, forkJoin, Observable, of, switchMap } from 'rxjs';
import { NotificationPreferencesService } from '../notification-preferences/notification-preferences.service';
import { PUSH_HANDLER, PushHandler } from './push-handler';
import { BaseNotificationsPreferences } from '../notification-preferences/base-notifications-preferences';
import { delay, filter, map, shareReplay, tap } from 'rxjs/operators';
import { StorageService } from '../app-state/storage.service';
import { PushData } from './push-data';
import { AppStateService } from '../app-state/app-state.service';
import { UserService } from '../user/user.service';
import { BaseUser } from '../user/models/user';

/**
 * Device Token flow:
 * Login (happens every time the app starts)
 *   Check if user has opted in to push notifications (opt-in is global for all accounts of the currently logged in user)
 *     if yes:
 *       get/generate token from FCM
 *       send api request to store device token for all users that could possibly log in to the current app (generic or customized) for the currently logged in user. The backend makes sure that no duplicate tokens get stored.
 *     if undefined:
 *       display modal and ask => if user wants to receive push notifications:
 *         if yes:
 *           get/generate token from FCM
 *           send api request to store device token for all users that could possibly log in to the current app (generic or customized) for the currently logged in user. The backend makes sure that no duplicate tokens get stored.
 *           store opt in locally
 * Logout
 *   send api request to delete token from backend
 *   delete token from FCM
 *
 * Notification Preferences
 *   enable:
 *     get/generate token from FCM
 *     send api request to store device token for all users that could possibly log in to the current app (generic or customized) for the currently logged in user. The backend makes sure that no duplicate tokens get stored.
 *     store opt in locally
 *   disable:
 *     send api request to delete token from backend
 *     delete token from FCM => no more messages will be received; token will get deleted in backend automatically on next (failing) send attempt. Thus we do not need to so send a delete api request to our backend
 *     store opt out locally
 */

const STORAGE_KEY_PREFIX = 'kgr_push_v3';

@Injectable({
  providedIn: 'root',
})
export class PushService {
  supported$ = this.pushHandler.supported$;
  permission$ = this.pushHandler.permission$;
  permissionGranted$ = this.pushHandler.permissionGranted$;
  enabled$ = new BehaviorSubject<boolean | undefined>(undefined);
  foregroundMessage$: Observable<PushData>;

  private permissionGranted = false;

  private foregroundMessageFilterCallback: (pushData: PushData) => boolean = () => true;

  private currentToken: string | null = null;

  constructor(
    @Inject(PUSH_HANDLER) private pushHandler: PushHandler,
    private preferencesService: NotificationPreferencesService<BaseNotificationsPreferences>,
    private userService: UserService<BaseUser>,
    private storage: StorageService,
    private appStateService: AppStateService,
  ) {
    this.readEnabledStatus().subscribe({
      next: (enabledStatus) => this.enabled$.next(enabledStatus),
    });

    this.permissionGranted$.subscribe((granted) => {
      this.permissionGranted = granted;
    });

    this.foregroundMessage$ = this.pushHandler.foregroundMessage$.pipe(
      filter((pushData) => this.foregroundMessageFilterCallback(pushData)),
      shareReplay(1),
    );
  }

  private storeTokenInBackend(token: string) {
    this.preferencesService.updatePushNotificationToken(token, this.pushHandler.getFirebaseProjectId()).subscribe({
      next: () => console.log('successfully updated token for all users'),
      error: (error) => console.error('failed to updated token for all users', error),
    });
  }

  private removeTokenFromBackend(token: string) {
    this.preferencesService.deletePushNotificationToken(token).subscribe({
      next: () => console.log('successfully deleted token for all users'),
      error: (error) => console.error('failed to delete token for all users', error),
    });
  }

  init(config: any): Observable<void> {
    return this.pushHandler.init(config).pipe(
      tap(() => {
        this.appStateService.show
          .pipe(
            delay(1000),
            switchMap(() => this.clearApplicationIconBadge()),
          )
          .subscribe(() => {
            // do nothing
          });
      }),
    );
  }

  register() {
    this.enabled$
      .pipe(
        filter((enabled) => !!enabled),
        switchMap(() => this.requestPermission()),
        filter((permission) => permission === 'granted'),
        switchMap(() => this.pushHandler.register()),
      )
      .subscribe({
        next: (token) => {
          if (token !== this.currentToken) {
            this.currentToken = token;
            this.storeTokenInBackend(token);
          }
        },
        error: (error) => {
          console.error('failed to register for push notifications', error);
        },
      });
  }

  unregister(): Observable<void> {
    return forkJoin([
      this.currentToken ? this.preferencesService.deletePushNotificationToken(this.currentToken) : of(null),
      this.pushHandler.unregister(),
    ]).pipe(map(() => undefined));
  }

  enable(): void {
    this.writeEnabledStatus(true);
    this.enabled$.next(true);
  }

  disable(): void {
    this.writeEnabledStatus(false);
    this.unregister().subscribe();
    this.enabled$.next(false);
  }

  clearApplicationIconBadge(): Observable<void> {
    return this.pushHandler.clearApplicationIconBadge();
  }

  requestPermission(): Observable<NotificationPermission> {
    return this.pushHandler.requestPermission();
  }

  isPermissionGranted(): boolean {
    return this.permissionGranted;
  }

  setForegroundMessageFilterCallback(callback: (pushData: PushData) => boolean): void {
    this.foregroundMessageFilterCallback = callback;
  }

  resetForegroundMessageFilterCallback(): void {
    this.foregroundMessageFilterCallback = () => true;
  }

  private readEnabledStatus(): Observable<boolean | undefined> {
    return this.storage.getItem(this.getStorageKey('enabled'), undefined).pipe(
      map((enabled) => {
        if (enabled === 'true') {
          return true;
        } else if (enabled === 'false') {
          return false;
        } else {
          return undefined;
        }
      }),
    );
  }

  private writeEnabledStatus(enabled: boolean): Observable<void> {
    return this.storage.setItem(this.getStorageKey('enabled'), enabled ? 'true' : 'false');
  }

  private readToken(): Observable<string | null> {
    return this.storage.getItem(this.getStorageKey('token'));
  }

  private getStorageKey(key: string): string {
    return `${STORAGE_KEY_PREFIX}_${key}`;
  }
}
